// Package renew / extend modals. Both hand an {apply, payment} to their
// onRenew/onExtend so the write runs against live App state.
import { useState } from "react";
import { clientToday, rolloverPackageHistory } from "../domain";
import { fmt, uid, pkgId } from "../lib/format";
import { pkgDisplayName } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Modal, Btn, Inp, Icon, useToast, useFormGuard } from "../ui/primitives";
import { PkgBuilder } from "../ui/forms";

export const ExtendPackageModal=({client,onClose,onExtend,theme,currency="₹"})=>{
  useFormGuard(); // pause background sync while this form is open
  const toast=useToast();
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const [days,setDays]=useState("");
  const [paid,setPaid]=useState("");

  const handleExtend=()=>{
    const extDays=parseInt(days)||0;
    if(extDays<1){toast("Enter the number of days to extend.","warning");return;}
    const amount=parseFloat(paid)||0;
    // apply() runs against the LIVE client in App state (C1), not this modal's
    // captured prop — read currentPackageId off `c` in case a renew landed while open.
    const apply=(c)=>({
      packageHistory:(c.packageHistory||[]).map(p=>
        p.packageId===c.currentPackageId
          ?{...p,extendedDays:(p.extendedDays||0)+extDays,extendedAt:clientToday()}
          :p),
      // legacy clients (no packageHistory) read client.package for expiry — bump
      // it there too, but NEVER inflate templateDurationDays.
      package:c.package?{...c.package,extendedDays:(c.package.extendedDays||0)+extDays}:c.package,
      // the extension payment counts toward the current package — keep client.paid
      // in step so balance/advance stay right and a later delete stays symmetric.
      ...(amount>0?{paid:(Number(c.paid)||0)+amount}:{}),
    });
    const payment=amount>0
      ?{id:uid(),clientId:client.id,clientName:client.name,amount,date:clientToday(),mode:"Cash",note:"Extension",kind:"extension",packageId:client.currentPackageId}
      :null;
    onExtend({apply,payment});
  };

  return(
    <Modal title={`Extend — ${client.name}`} onClose={onClose} theme={theme}>
      <div className="space-y-4">
        <div className="rounded-xl p-3 border" style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc}}>
          <p className="text-xs font-semibold mb-1" style={{color:mc}}>Current Package</p>
          <p className="text-sm font-bold" style={{color:tc}}>
            {pkgDisplayName(client.package)}
          </p>
          {client.package?.startDate&&<p className="text-xs mt-0.5" style={{color:mc}}>Started: {client.package.startDate}</p>}
        </div>
        <Inp label="Extend by (days) *" type="number" value={days} onChange={e=>setDays(e.target.value)} theme={theme} placeholder="e.g. 15"/>
        <Inp label={`Additional Payment (${currency}) — optional`} type="number" value={paid} onChange={e=>setPaid(e.target.value)} theme={theme} placeholder="0"/>
        <Btn onClick={handleExtend} theme={theme}><Icon name="check" size={16}/>Confirm Extension</Btn>
      </div>
    </Modal>
  );
};

export const RenewPackageModal=({client,onClose,onRenew,theme,currency="₹",profile})=>{
  useFormGuard(); // pause background sync while this form is open
  const toast=useToast();
  const {isDark,tc,mc,bc}=themeTokens(theme);
  const [pkg,setPkg]=useState({...client.package,startDate:clientToday()});
  const [paid,setPaid]=useState(0);

  const handleRenew=()=>{
    if(!pkg.startDate){toast("Pick a start date.","warning");return;}
    if(pkg.startDate>clientToday()){toast("Start date can't be in the future.","warning");return;}
    const newId=pkgId();
    const price=Number(pkg.totalAmount)||0;
    const amount=Number(paid)||0;
    const total=(Number(pkg.customClasses)||0)||(Number(pkg.templateSessions)||0);
    const cleanPkg={...pkg,extendedDays:0};

    // apply() runs against the LIVE client in App state (C1). rolloverPackageHistory
    // is the shared "prior -> renewed, append new active entry" transition.
    const apply=(c)=>({
      ...rolloverPackageHistory(c,pkg,newId,clientToday()),
      packagePrice:price,
      paid:amount,          // renewal payment is the only payment for the new package; clean slate
      classesAttended:0,
      classesRemaining:total>0?total:0,
      package:{...cleanPkg,totalAmount:price},
    });
    const payment=amount>0
      ?{id:uid(),clientId:client.id,clientName:client.name,amount,date:clientToday(),mode:"Cash",note:"Renewal",kind:"renewal",packageId:newId}
      :null;   // payment.date = transaction date (today), NOT pkg.startDate
    onRenew({apply,payment});
  };

  return(
    <Modal title={`Renew — ${client.name}`} onClose={onClose} theme={theme}>
      <div className="space-y-4">
        <div className="rounded-xl p-3 border" style={{background:isDark?"#0f172a":"#f9fafb",borderColor:bc}}>
          <p className="text-xs font-semibold mb-1" style={{color:mc}}>Previous Package</p>
          <p className="text-sm font-bold" style={{color:tc}}>{pkgDisplayName(client.package)} · {fmt(client.packagePrice||0,currency)}</p>
          {client.package?.startDate&&<p className="text-xs" style={{color:mc}}>Started: {client.package.startDate}</p>}
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-wider" style={{color:mc}}>New Start Date *</label>
          <input type="date" max={clientToday()} className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none"
            style={{background:isDark?"#0f172a":"#fff",borderColor:bc,color:tc}}
            value={pkg.startDate||clientToday()} onChange={e=>setPkg(p=>({...p,startDate:e.target.value}))}/>
        </div>
        <PkgBuilder pkg={pkg} onChange={setPkg} theme={theme} currency={currency} profile={profile}/>
        <Inp label={`Payment Received (${currency})`} type="number" value={paid||""}
          onChange={e=>setPaid(Number(e.target.value))} theme={theme} placeholder="0"/>
        <Btn onClick={handleRenew} theme={theme}><Icon name="check" size={16}/>Confirm Renewal</Btn>
      </div>
    </Modal>
  );
};
